/**
 * `submittal.tmpl:156-289` — the Application-library detail panes.
 *
 * The template is one flat chain of 21 `{{#ifCompare value '==' '<slug>'}}`
 * blocks, each emitting a single `<div>` wrapping one or more `<img>`s. This
 * table is that chain, transcribed key-for-key and filename-for-filename.
 *
 * ★ TWO FILENAMES LOOK LIKE TYPOS AND ARE NOT — they are the real files on
 * disk and the legacy `src` strings, so they are copied byte-for-byte:
 *   · `elevation_intake`     → "Elevation Intake.jpg"   (a SPACE, not "_")
 *   · `evap_pack_selection`  → "evap_pack _selection.jpg" (a SPACE before "_selection")
 * "Fixing" either one turns a working image into a 404.
 *
 * ★ NOT PORTED — `er_wheel_selection` (`submittal.tmpl:281-285`). Proven dead
 * (discovery §10): the branch renders `<div class="hidden"><img src="/static/
 * user_settings/images/"/></div>`, and the backend viewset
 * (`rest_master/user_settings_viewset.py:63`) `.exclude(value__in=
 * ['er_wheel_selection'])`, so the row never reaches the client in the first
 * place. A slug with no entry here renders an empty `<div class="col-xs-12
 * <value>">`, which is exactly what the legacy chain does for an unmatched
 * value — so the omission is behaviour-preserving even if the row ever arrives.
 *
 * ★ Q14 lives one level up, in the data: `master_submittal_predefined_field`
 * has TWO rows named "Center of Gravity Calc" (ids 20 and 21) sharing one
 * `value`, so two panes end up with the same DOM id. Nothing here can or should
 * de-duplicate that (discovery §12 R12 — a data fix for the owner).
 */
const IMAGE_BASE = '/static/user_settings/images/';

export const APP_PREVIEW_IMAGES: Record<string, string[]> = {
  plan: ['Plan.jpg'],
  elevation_far_side: ['Elevation_Far_Side.jpg'],
  elevation_intake: ['Elevation Intake.jpg'],
  elevation_discharge: ['Elevation_Discharge.jpg'],
  elevation_near_side: ['Elevation_NS.jpg'],
  submittal_cover: ['submittal_cover.jpg'],
  '2d_wireframe_all_views': ['2d_wireframe_all_views.jpg'],
  '2d_wireframe_plan_and_elevation_near_side': ['2D_plan_view.jpg'],
  '3d_render': ['3_d.png'],
  equipment_list: ['equipment_list.jpg'],
  unit_technical_data: [
    'unit_technical_data_page-0001.jpg',
    'unit_technical_data_page-0002.jpg',
    'unit_technical_data_page-0003.jpg',
    'unit_technical_data_page-0004.jpg',
    'unit_technical_data_page-0005.jpg',
    'unit_technical_data_page-0006.jpg',
    'unit_technical_data_page-0007.jpg',
  ],
  model_view_3d: ['model_view_3D.png'],
  model_view_2d: ['model_view_2D.png', 'model_view_2D_elevation_NS.png'],
  pressure_drop_calc: ['pressure_drop_calc.jpg'],
  fan_data: ['fan_selection_page-0001.jpg'],
  coil_data: ['coil_selection_page-0001.jpg'],
  gas_heater_selection_data: ['gas_heater_selection.jpg'],
  weight_calc: [
    'weight_calc_page-0001.jpg',
    'weight_calc_page-0002.jpg',
    'weight_calc_page-0003.jpg',
    'weight_calc_page-0004.jpg',
    'weight_calc_page-0005.jpg',
  ],
  electrical_calc: ['electrical_calc_page-0001.jpg'],
  evap_pack_selection: ['evap_pack _selection.jpg'],
};

/** Absolute `src`s for one Application slug, or `[]` when the template's chain
 * has no branch for it. */
export const appPreviewImageSrcs = (value: string): string[] =>
  (APP_PREVIEW_IMAGES[value] ?? []).map((file) => `${IMAGE_BASE}${file}`);
